import { useMemo } from 'react';
import ky, { type KyInstance } from 'ky';
import { config } from '../config';
import { getToken, logout } from '../utils/token';

/** Logout via legacy admin so 401 response acts as logout; redirect to login with session-expired message. */
const sessionExpiredUrl = `${config.NEW_ADMIN_UI_URL}/login?showTimeoutMessage=true`;

/**
 * Default timeout for API requests in milliseconds
 * 30 seconds - sufficient for most operations, prevents hanging requests
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * Default retry configuration
 * - Disabled by default to prevent double retries with TanStack Query
 * - TanStack Query handles retries at the query/mutation level
 */
const DEFAULT_RETRY_CONFIG = {
  limit: 0, // Disabled - retries handled by TanStack Query
};

/**
 * Creates an authenticated API client that automatically adds the Bearer token
 * and handles 401 responses by redirecting to login.
 * This should be used for all API calls in the admin-ui.
 *
 * @example
 * ```typescript
 * const api = apiClient();
 * const user = await api.get('find/api/users/123').json();
 * ```
 */
export const apiClient = (): KyInstance => {
  const token = getToken();
  if (!token) {
    window.location.href = `${config.NEW_ADMIN_UI_URL}/login`;
    throw new Error('No authentication token found. Redirecting to login...');
  }

  // Use Admin API directly if configured, otherwise use gateway
  const baseUrl = config.USE_DIRECT_ADMIN_API ? config.ADMIN_API_URL : config.GATEWAY_URL;

  return ky.create({
    prefixUrl: baseUrl,
    timeout: DEFAULT_TIMEOUT,
    retry: DEFAULT_RETRY_CONFIG,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    hooks: {
      afterResponse: [
        (_request, _options, response) => {
          if (response.status === 401) {
            logout(sessionExpiredUrl);
          }
        },
      ],
    },
  });
};

/**
 * Creates an authenticated API client with custom timeout
 * Use when you need a different timeout for specific operations
 *
 * @example
 * ```typescript
 * const longRunningClient = apiClientWithTimeout(60000); // 60 seconds
 * const result = await longRunningClient.get('api/long-operation').json();
 * ```
 */
export const apiClientWithTimeout = (timeoutMs: number): KyInstance => {
  const api = apiClient();
  return api.extend({
    timeout: timeoutMs,
  });
};

/**
 * Creates an authenticated API client with custom retry configuration
 * Use when you need different retry behavior for specific operations
 *
 * @example
 * ```typescript
 * const noRetryClient = apiClientWithRetry({ limit: 0 });
 * const result = await noRetryClient.get('api/no-retry-endpoint').json();
 * ```
 */
export const apiClientWithRetry = (retryConfig: Partial<typeof DEFAULT_RETRY_CONFIG>): KyInstance => {
  const api = apiClient();
  return api.extend({
    retry: {
      ...DEFAULT_RETRY_CONFIG,
      ...retryConfig,
    },
  });
};

/**
 * Hook to get an authenticated API client instance
 * This is the recommended way to use the API client in React components
 *
 * @example
 * ```typescript
 * const api = useApiClient();
 * const user = await api.get('find/api/users/123').json();
 * ```
 */
export const useApiClient = (): KyInstance => {
  return useMemo(() => apiClient(), []);
};

/**
 * Creates an authenticated API client for the Insights Agent backend
 * Uses INSIGHTS_AGENT_URL as the base URL with reduced retry limits
 */
export const createInsightsApiClient = (): KyInstance => {
  return ky.create({
    prefixUrl: config.INSIGHTS_AGENT_URL,
    timeout: DEFAULT_TIMEOUT,
    retry: {
      ...DEFAULT_RETRY_CONFIG,
      limit: 2,
    },
    headers: {
      'Content-Type': 'application/json',
    },
    hooks: {
      beforeRequest: [
        (request) => {
          const token = getToken();
          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
        },
      ],
    },
  });
};

export const useInsightsApiClient = (): KyInstance => {
  return useMemo(() => createInsightsApiClient(), []);
};
